import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import type { Setting } from '@/types';
import { Check, X } from 'lucide-react';

type RoleTranslationItem = {
    value: string;
    label: string;
    label_lt?: string;
    enabled: boolean;
};

interface ProjectRoleTranslationsEditorProps {
    settingKey: string;
    setting: Setting;
    availableRoles: string[];
    projectTaskDepartments?: Array<{ value: string; label?: string; roles?: string[] }>;
    onUpdate: (key: string, value: RoleTranslationItem[]) => void;
}

function normalizeTranslations(value: unknown, availableRoles: string[]): RoleTranslationItem[] {
    const byRole = new Map<string, RoleTranslationItem>();

    if (Array.isArray(value)) {
        value.forEach((item) => {
            if (!item || typeof item !== 'object') {
                return;
            }

            const record = item as Record<string, unknown>;
            const roleName = typeof record.value === 'string' ? record.value : '';
            if (!roleName) {
                return;
            }

            byRole.set(roleName, {
                value: roleName,
                label: typeof record.label === 'string' ? record.label : roleName,
                label_lt: typeof record.label_lt === 'string' ? record.label_lt : '',
                enabled: record.enabled !== false,
            });
        });
    }

    return availableRoles.map((roleName) => {
        const existing = byRole.get(roleName);
        if (existing) {
            return existing;
        }

        return {
            value: roleName,
            label: roleName,
            label_lt: '',
            enabled: true,
        };
    });
}

export function ProjectRoleTranslationsEditor({
    settingKey,
    setting,
    availableRoles,
    projectTaskDepartments = [],
    onUpdate,
}: ProjectRoleTranslationsEditorProps) {
    const { t } = useTranslations();
    const roleItems = useMemo(
        () =>
            normalizeTranslations(
                setting.value,
                [...availableRoles].sort((a, b) => a.localeCompare(b)),
            ),
        [setting.value, availableRoles],
    );

    const roleDepartments = useMemo(() => {
        const map = new Map<string, string[]>();
        roleItems.forEach((item) => map.set(item.value, []));

        projectTaskDepartments.forEach((department) => {
            const departmentLabel = (department.label || department.value || '').trim();
            if (!departmentLabel || !Array.isArray(department.roles)) {
                return;
            }

            department.roles.forEach((roleName) => {
                const existing = map.get(roleName) ?? [];
                if (!existing.includes(departmentLabel)) {
                    existing.push(departmentLabel);
                }
                map.set(roleName, existing);
            });
        });

        return map;
    }, [projectTaskDepartments, roleItems]);

    const [drafts, setDrafts] = useState<Record<string, string>>({});

    const getInputValue = (item: RoleTranslationItem): string => {
        if (Object.prototype.hasOwnProperty.call(drafts, item.value)) {
            return drafts[item.value];
        }

        return item.label_lt ?? '';
    };

    const hasRowChanges = (item: RoleTranslationItem): boolean => getInputValue(item) !== (item.label_lt ?? '');

    const updateDraft = (roleName: string, lithuanianLabel: string): void => {
        setDrafts((previous) => ({ ...previous, [roleName]: lithuanianLabel }));
    };

    const cancelDraft = (roleName: string): void => {
        setDrafts((previous) => {
            const next = { ...previous };
            delete next[roleName];
            return next;
        });
    };

    const saveDraft = (roleName: string): void => {
        const roleItem = roleItems.find((item) => item.value === roleName);
        if (!roleItem) {
            return;
        }

        const valueToSave = getInputValue(roleItem);
        const next = roleItems.map((item) => (item.value === roleName ? { ...item, label_lt: valueToSave } : item));
        onUpdate(settingKey, next);
        cancelDraft(roleName);
    };

    // Visibility saves immediately; a pending Lithuanian-label draft is intentionally
    // left untouched so a toggle never writes an unconfirmed label edit.
    const toggleEnabled = (roleName: string, isEnabled: boolean): void => {
        const next = roleItems.map((item) => (item.value === roleName ? { ...item, enabled: isEnabled } : item));
        onUpdate(settingKey, next);
    };

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <div className="text-sm font-medium">{t('settings.project_role_translations.title', 'Role translations (Lithuanian)')}</div>
                <div className="text-muted-foreground text-xs">
                    {t(
                        'settings.project_role_translations.description',
                        'Set Lithuanian labels for roles used in department/task UIs, and choose which roles appear in the task department list.',
                    )}
                </div>
            </div>

            <div className="space-y-3">
                {roleItems.map((item) => (
                    <div key={item.value} className="grid gap-2 md:grid-cols-2">
                        <div className="space-y-1">
                            <Label>{t('settings.project_role_translations.role', 'Role')}</Label>
                            <Input value={item.label || item.value} readOnly />
                            <div className="flex items-center justify-between gap-2 pt-1">
                                <Label htmlFor={`role-visibility-${item.value}`} className="text-muted-foreground text-xs font-normal">
                                    {t('settings.project_role_translations.show_in_task_departments', 'Show in task departments')}
                                </Label>
                                <Switch
                                    id={`role-visibility-${item.value}`}
                                    checked={item.enabled}
                                    onCheckedChange={(nextChecked) => toggleEnabled(item.value, nextChecked)}
                                />
                            </div>
                            <div className="flex flex-wrap gap-1 pt-1">
                                {(roleDepartments.get(item.value) ?? []).map((departmentLabel) => (
                                    <span
                                        key={`${item.value}-${departmentLabel}`}
                                        className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs"
                                    >
                                        {departmentLabel}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label>{t('settings.project_role_translations.lithuanian_label', 'Lithuanian label')}</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    value={getInputValue(item)}
                                    onChange={(e) => updateDraft(item.value, e.target.value)}
                                    placeholder={t('settings.project_role_translations.lithuanian_label_placeholder', 'e.g., Aptarnavimo vadovas')}
                                />
                                {hasRowChanges(item) && (
                                    <>
                                        <Button type="button" size="icon" variant="outline" onClick={() => saveDraft(item.value)}>
                                            <Check className="h-4 w-4" />
                                        </Button>
                                        <Button type="button" size="icon" variant="ghost" onClick={() => cancelDraft(item.value)}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
